/**
 * EditProfile Page - Edit user profile
 * Based on mobile app EditProfile.tsx
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Camera, Check, Shield, X, ZoomIn, ZoomOut } from 'lucide-react'
import Cropper from 'react-easy-crop'
import Avatar from '../components/ui/Avatar'
import api from '../services/api'

// Types for react-easy-crop
interface Point {
  x: number
  y: number
}

interface Area {
  width: number
  height: number
  x: number
  y: number
}

// Helper function to create cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.setAttribute('crossOrigin', 'anonymous')
    image.src = url
  })

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area
): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Could not get canvas context')
  }

  // Set canvas size to cropped area
  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height

  // Draw the cropped image
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  )

  // Convert canvas to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas is empty'))
        return
      }
      resolve(blob)
    }, 'image/jpeg', 0.95)
  })
}

export default function EditProfile() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    bio: '',
    avatar: '',
    occupation: '',
    dateOfBirth: '',
    isReporter: false,
    verified: false,
  })

  const [originalData, setOriginalData] = useState(formData)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)

  // Cropper states
  const [showCropModal, setShowCropModal] = useState(false)
  const [imageToCrop, setImageToCrop] = useState<string>('')
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setIsLoadingProfile(true)
      const profile = await api.getUserProfile()

      if (profile) {
        const profileData = {
          name: profile.name || '',
          username: profile.username || '',
          email: profile.email || '',
          bio: profile.bio || '',
          avatar: profile.avatar || '',
          occupation: profile.occupation || '',
          dateOfBirth: profile.dateOfBirth || '',
          isReporter: profile.isReporter || false,
          verified: profile.verified || false,
        }
        setFormData(profileData)
        setOriginalData(profileData)
      }
    } catch (error) {
      console.error('Error loading profile:', error)
      alert('Failed to load profile data')
    } finally {
      setIsLoadingProfile(false)
    }
  }

  // Check if form has unsaved changes
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length < 2 || formData.name.length > 50) {
      newErrors.name = 'Name must be 2-50 characters'
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required'
    } else if (formData.username.length < 3 || formData.username.length > 15) {
      newErrors.username = 'Username must be 3-15 characters'
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores'
    }

    // Email validation (optional)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }

    // Bio validation
    if (formData.bio && formData.bio.length > 160) {
      newErrors.bio = 'Bio must be 160 characters or less'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      console.log('💾 Saving profile changes...')
      const result = await api.updateUserProfile({
        name: formData.name,
        username: formData.username,
        email: formData.email,
        bio: formData.bio,
        occupation: formData.occupation,
      })

      console.log('📥 Update result:', result)

      if (result.success) {
        // Update original data to prevent unsaved changes warning
        setOriginalData(formData)
        alert('Profile updated successfully!')
        navigate('/profile')
      } else {
        const errorMsg = result.message || 'Failed to update profile'
        console.error('❌ Profile update failed:', errorMsg)
        alert(errorMsg)
      }
    } catch (error) {
      console.error('Save profile error:', error)
      const errorMsg = error instanceof Error ? error.message : 'Failed to update profile'
      alert(`Error: ${errorMsg}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  // Crop callback
  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB')
      return
    }

    // Open crop modal
    setSelectedFile(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      const imageDataUrl = reader.result as string
      setImageToCrop(imageDataUrl)
      setShowCropModal(true)
    }
    reader.readAsDataURL(file)
  }

  const handleCropConfirm = async () => {
    if (!croppedAreaPixels || !imageToCrop) return

    try {
      setIsLoading(true)
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels)

      // Convert blob to file for upload
      const croppedFile = new File([croppedBlob], selectedFile?.name || 'profile.jpg', {
        type: 'image/jpeg',
      })

      // Upload the cropped image
      const result = await api.uploadProfileImage(croppedFile)

      if (result.success && result.url) {
        setFormData(prev => ({ ...prev, avatar: result.url! }))
        setShowCropModal(false)
        alert('Profile image updated successfully!')
      } else {
        alert(result.message || 'Failed to upload image')
      }
    } catch (error) {
      console.error('Avatar upload error:', error)
      alert('Failed to upload image')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCropCancel = () => {
    setShowCropModal(false)
    setImageToCrop('')
    setSelectedFile(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }


  const handleBackClick = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(-1)
      }
    } else {
      navigate(-1)
    }
  }

  const bioCharCount = formData.bio.length
  const bioCharLimit = 160

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackClick}
              className="text-gray-700 hover:text-onrecord-orange rounded-full p-1 hover:bg-orange-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold">Edit Profile</h1>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
            className={`btn btn-primary ${(!hasChanges || isLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-2xl mx-auto pb-6">
        {/* Avatar Section */}
        <div className="flex justify-center py-8 px-4">
          <div className="relative">
            <Avatar
              src={formData.avatar}
              alt={formData.name}
              size="2xl"
              className="cursor-pointer"
              onClick={handleAvatarClick}
            />
            {/* Camera Overlay */}
            <button
              onClick={handleAvatarClick}
              className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-onrecord-orange flex items-center justify-center text-white hover:bg-onrecord-orange-dark transition-colors shadow-lg"
            >
              <Camera className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleAvatarChange}
          className="hidden"
        />

        {/* Form Fields */}
        <div className="space-y-6 px-4">
          {/* Name */}
          <div>
            <label className="block text-post font-medium mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              maxLength={50}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange ${
                errors.name ? 'border-error' : 'border-gray-300'
              }`}
              placeholder="Enter your name"
            />
            {errors.name && (
              <p className="text-small text-error mt-1">{errors.name}</p>
            )}
            <p className="text-tiny text-text-secondary mt-1">
              {formData.name.length}/50
            </p>
          </div>

          {/* Username */}
          <div>
            <label className="block text-post font-medium mb-2">
              Username *
            </label>
            <div className="relative">
              <span className="absolute left-4 top-3.5 text-text-secondary">@</span>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                maxLength={15}
                className={`w-full pl-8 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange ${
                  errors.username ? 'border-error' : 'border-gray-300'
                }`}
                placeholder="username"
              />
            </div>
            {errors.username && (
              <p className="text-small text-error mt-1">{errors.username}</p>
            )}
            <p className="text-tiny text-text-secondary mt-1">
              {formData.username.length}/15 • Letters, numbers, and underscores only
            </p>
          </div>

          {/* Email */}
          <div>
            <label className="block text-post font-medium mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange ${
                errors.email ? 'border-error' : 'border-gray-300'
              }`}
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="text-small text-error mt-1">{errors.email}</p>
            )}
          </div>

          {/* Bio */}
          <div>
            <label className="block text-post font-medium mb-2">
              Bio
            </label>
            <textarea
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              maxLength={bioCharLimit}
              rows={3}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange resize-none ${
                errors.bio ? 'border-error' : 'border-gray-300'
              }`}
              placeholder="Tell people about yourself..."
            />
            {errors.bio && (
              <p className="text-small text-error mt-1">{errors.bio}</p>
            )}
            <p className={`text-tiny mt-1 ${bioCharCount > bioCharLimit - 20 ? 'text-warning' : 'text-text-secondary'}`}>
              {bioCharCount}/{bioCharLimit}
            </p>
          </div>

          {/* Occupation */}
          <div>
            <label className="block text-post font-medium mb-2">
              Occupation
            </label>
            <input
              type="text"
              value={formData.occupation}
              onChange={(e) => handleInputChange('occupation', e.target.value)}
              maxLength={50}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-onrecord-orange"
              placeholder="What do you do?"
            />
            <p className="text-tiny text-text-secondary mt-1">
              {formData.occupation.length}/50
            </p>
          </div>

          {/* Date of Birth (Read-only) */}
          {formData.dateOfBirth && (
            <div>
              <label className="block text-post font-medium mb-2">
                Date of Birth
              </label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
                {new Date(formData.dateOfBirth).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
              <p className="text-tiny text-text-secondary mt-1">
                Date of birth cannot be changed
              </p>
            </div>
          )}

          {/* Reporter Badge (Read-only) */}
          {formData.isReporter && (
            <div className="flex items-start gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-post font-medium text-blue-900">Verified Reporter</p>
                <p className="text-small text-blue-700">You have access to professional journalism tools</p>
              </div>
            </div>
          )}

          {/* Verification Badge (Read-only) */}
          {formData.verified && (
            <div className="flex items-center gap-2 px-4 py-3 bg-onrecord-orange-bg rounded-xl">
              <div className="w-6 h-6 bg-onrecord-orange rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <div>
                <p className="text-post font-medium text-onrecord-orange">Verified Account</p>
                <p className="text-small text-text-secondary">Your account is verified</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Crop Profile Photo</h2>
              <button
                onClick={handleCropCancel}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Cropper */}
            <div className="relative bg-gray-100" style={{ height: '400px' }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Controls */}
            <div className="px-6 py-4 space-y-4 bg-white">
              {/* Zoom Slider */}
              <div className="flex items-center gap-4">
                <ZoomOut className="w-5 h-5 text-gray-400" />
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <ZoomIn className="w-5 h-5 text-gray-400" />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCropCancel}
                  className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCropConfirm}
                  disabled={isLoading}
                  className="flex-1 py-3 bg-onrecord-orange hover:bg-onrecord-orange-dark text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Uploading...' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
